/**
 * Client-side validation for portable campaign JSON file import (SOC2 / secure SDLC friendly):
 * size cap, extension allowlist, MIME allowlist, UTF-8 text read, JSON parse, object root.
 * Server-side validation remains authoritative.
 */

export const PORTABLE_IMPORT_MAX_FILE_BYTES = 2 * 1024 * 1024; // 2 MiB

const JSON_EXTENSION = '.json';

const MIME_ALLOWLIST = new Set([
    'application/json',
    'text/json',
    'text/plain',
    'application/octet-stream',
    '',
]);

/**
 * @param {File} file
 * @returns {{ ok: true } | { ok: false, error: string }}
 */
export function validatePortableImportFileMetadata(file) {
    if (!file || typeof file !== 'object') {
        return { ok: false, error: 'No file selected.' };
    }
    if (typeof file.size !== 'number' || file.size < 0) {
        return { ok: false, error: 'Invalid file.' };
    }
    if (file.size === 0) {
        return { ok: false, error: 'File is empty.' };
    }
    if (file.size > PORTABLE_IMPORT_MAX_FILE_BYTES) {
        const mb = PORTABLE_IMPORT_MAX_FILE_BYTES / (1024 * 1024);
        return { ok: false, error: `File is too large. Maximum size is ${mb} MB.` };
    }

    const name = typeof file.name === 'string' ? file.name : '';
    const base = name.replace(/^.*[/\\]/, '');
    if (!base.toLowerCase().endsWith(JSON_EXTENSION)) {
        return { ok: false, error: 'Only .json files are allowed.' };
    }
    if (base.length > 255) {
        return { ok: false, error: 'File name is too long.' };
    }

    const mime = typeof file.type === 'string' ? file.type.toLowerCase().trim() : '';
    if (mime && !MIME_ALLOWLIST.has(mime)) {
        return { ok: false, error: 'Invalid file type. Use a .json file.' };
    }

    return { ok: true };
}

function stripUtf8Bom(text) {
    if (typeof text !== 'string' || text.length === 0) {
        return text;
    }
    if (text.charCodeAt(0) === 0xfeff) {
        return text.slice(1);
    }
    return text;
}

/**
 * Read file as UTF-8 text only (no array buffer / binary paths for JSON text).
 * @param {File} file
 * @returns {Promise<string>}
 */
export function readFileAsUtf8Text(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === 'string') {
                resolve(reader.result);
                return;
            }
            reject(new Error('Could not read file as text.'));
        };
        reader.onerror = () => reject(new Error('Failed to read file.'));
        reader.readAsText(file, 'UTF-8');
    });
}

/**
 * Validate metadata, read UTF-8, parse JSON, require plain object (not array).
 * @param {File} file
 * @returns {Promise<{ ok: true, data: object } | { ok: false, error: string }>}
 */
export async function readAndValidatePortableImportJsonFile(file) {
    const meta = validatePortableImportFileMetadata(file);
    if (!meta.ok) {
        return { ok: false, error: meta.error };
    }

    let text;
    try {
        text = await readFileAsUtf8Text(file);
    } catch (e) {
        return { ok: false, error: e?.message || 'Failed to read file.' };
    }

    const trimmed = stripUtf8Bom(text).trim();
    if (!trimmed) {
        return { ok: false, error: 'File contains no JSON.' };
    }
    const first = trimmed[0];
    if (first !== '{' && first !== '[') {
        return { ok: false, error: 'JSON must begin with { or [.' };
    }

    let parsed;
    try {
        parsed = JSON.parse(trimmed);
    } catch {
        return { ok: false, error: 'Invalid JSON syntax.' };
    }

    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { ok: false, error: 'Portable import requires a JSON object (not an array).' };
    }

    return { ok: true, data: parsed };
}
